




import { mapActions, mapState } from 'vuex';
import Dropzone from 'dropzone';
import 'dropzone/dist/dropzone.css';
import { MetadataType } from '@adobe/edex/ui/shared/frontend/utils/types';
import { CDN_UPLOADS_FOLDER } from '@adobe/edex/ui/shared/frontend/utils/cdn';
import * as fileAPI from '@adobe/edex/frontend/api/file';

Dropzone.autoDiscover = false;

export default {
  name: 'dropzone',
  props: [
    'entityType',
    'entityID',
    'fileType',
    'singleFileMode',
    'acceptedFiles',
    'maxFilesize',
  ],
  methods: {
    ...mapActions('metadata', ['fetchByType']),
  },
  computed: {
    ...mapState('metadata', ['fileTypes']),
  },
  data() {
    return {
      filesInProgress: {},
    };
  },
  async mounted() {
    await this.fetchByType(MetadataType.fileTypes);

    const options = {
      url: '/',
      method: 'put',
      autoQueue: true,
      autoProcessQueue: false,
      acceptedFiles: this.acceptedFiles || null,
      uploadMultiple: true,
      maxFiles: null,
      maxFilesize: this.maxFilesize || 50,
      // For reference: https://github.com/kfei/vue-s3-dropzone/blob/master/frontend/src/components/Dropzone.vue
      sending: (file, xhr) => {
        const _send = xhr.send;
        xhr.setRequestHeader(
          'Content-Type',
          file.type !== '' ? file.type : 'application/octet-stream'
        );
        xhr.send = () => {
          _send.call(xhr, file);
        };
      },
      accept: async (file, done) => {
        const fileExtension = file.name.split('.').pop();
        const newFilename = `${file.upload.uuid}.${fileExtension}`;
        const objectPath = `${CDN_UPLOADS_FOLDER}/${this.fileType}/${this.constants.currentYear}/${this.entityID}/${newFilename}`;

        const fileParams = {
          label: file.name,
          originalFileName: file.name,
          entityType: this.entityType,
          entityID: this.entityID,
          fileTypeID: this.fileTypes.byLabel[this.fileType],
          storageOrigin: 's3',
          storagePath: objectPath,
          size: file.size,
          mimeType: file.type,
        };

        const res = await fileAPI.create(fileParams);

        this.filesInProgress[file.upload.uuid] = res.data.file;

        file.uploadURL = res.data.url;

        done();

        // Manually process each file
        setTimeout(() => this.dropzone.processFile(file));
      },
      success: async file => {
        let res = { ...this.filesInProgress[file.upload.uuid] };
        // in case of new resource - postpone status update
        if (this.entityID) {
          ({ data: res } = await fileAPI.update(
            this.filesInProgress[file.upload.uuid].id,
            {
              status: 'active',
            }
          ));
        }
        delete this.filesInProgress[file.upload.uuid];
        this.$emit('onUploadCompletion', { file: res });
      },
      error: async (file, errorMessage, done) => {
        if (file.status === 'error') {
          // Set the error mode+message manually
          file.previewElement.classList.add('dz-error');
          file.previewElement.querySelector(
            '[data-dz-errormessage]'
          ).innerHTML = errorMessage;
        } else if (this.filesInProgress[file.upload.uuid]) {
          await fileAPI.remove(this.filesInProgress[file.upload.uuid].id);
        }
        return;
      },
      canceled: async file => {
        if (this.filesInProgress[file.upload.uuid]) {
          await fileAPI.remove(this.filesInProgress[file.upload.uuid].id);
        }
      },
    };

    if (this.singleFileMode) {
      options.uploadMultiple = false;
      options.maxFiles = 1;
    }

    this.dropzone = new Dropzone(this.$el, options);

    // Set signed upload URL for each file
    this.dropzone.on('processing', file => {
      this.dropzone.options.url = file.uploadURL;
    });
  },
};
